import React, { useState, useEffect, useCallback } from 'react';
import './ExamEligibility.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaArrowLeft,
  FaSpinner,
  FaBookMedical,
  FaHospital,
  FaAward,
  FaGraduationCap,
  FaNewspaper,
  FaChalkboardTeacher,
  FaUsers,
  FaClipboardList,
  FaSyncAlt
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

const StatusIcon = ({ passed, notApplicable }) => {
  if (notApplicable) return <span className="ee-icon ee-icon-na" title="Not applicable"><FaExclamationTriangle /></span>;
  return passed
    ? <span className="ee-icon ee-icon-pass" title="Eligible"><FaCheckCircle /></span>
    : <span className="ee-icon ee-icon-fail" title="Not eligible"><FaTimesCircle /></span>;
};

const CheckRow = ({ icon, title, description, passed, notApplicable, detail }) => (
  <div className={`ee-check-row ${passed || notApplicable ? 'pass' : 'fail'}`}>
    <div className="ee-check-left">
      <span className="ee-check-icon">{icon}</span>
      <div className="ee-check-text">
        <span className="ee-check-title">{title}</span>
        {description && <span className="ee-check-desc">{description}</span>}
        {detail && <span className="ee-check-detail">{detail}</span>}
      </div>
    </div>
    <StatusIcon passed={passed} notApplicable={notApplicable} />
  </div>
);

const ExamEligibility = ({ onNavigate }) => {
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchEligibility = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/exam-eligibility/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setEligibility(data.eligibility);
      } else {
        setError(data.message || 'Failed to load eligibility data');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEligibility(); }, [fetchEligibility]);

  if (loading) {
    return (
      <div className="ee-container">
        <div className="ee-loading">
          <FaSpinner className="ee-spinner" />
          <p>Checking your exam eligibility...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ee-container">
        <div className="ee-header">
          <button className="ee-back-btn" onClick={() => onNavigate('dashboard')}>
            <FaArrowLeft /> Back
          </button>
          <h2>Exam Eligibility</h2>
        </div>
        <div className="ee-error">
          <FaExclamationTriangle /> {error}
        </div>
      </div>
    );
  }

  if (!eligibility) return null;

  const { checks, overallEligible } = eligibility;

  const missingItems = [];
  if (checks.weeklyLogbook.applicable !== false && !checks.weeklyLogbook.passed) missingItems.push('Regular weekly logbook submissions');
  if (!checks.drpCompletion.passed) missingItems.push('DRP completion certificate');
  if (!checks.certificates.researchMethodology.passed) missingItems.push('Research Methodology certificate');
  if (!checks.certificates.ethicsCourse.passed) missingItems.push('Ethics Course certificate');
  if (!checks.certificates.bclsAcls.passed) missingItems.push('BCLS & ACLS certificate');
  if (!checks.dissertation.passed) {
    missingItems.push(checks.dissertation.totalPeriods > 0
      ? `Dissertation (all ${checks.dissertation.totalPeriods} submitted progress reports graded)`
      : 'Dissertation (submit at least one progress report)');
  }
  if (!checks.publications.passed) missingItems.push('Research publication/presentation');
  if (checks.seminarsActivities.applicable && !checks.seminarsActivities.passed) missingItems.push('Seminar/Journal club/Clinical meeting entries');
  if (checks.ugTeaching.applicable && !checks.ugTeaching.passed) missingItems.push('UG teaching participation');

  return (
    <div className="ee-container">
      <div className="ee-header">
        <button className="ee-back-btn" onClick={() => onNavigate('dashboard')}>
          <FaArrowLeft /> Back
        </button>
        <h2>Exam Eligibility</h2>
        <button className="ee-refresh-btn" onClick={fetchEligibility} title="Refresh">
          <FaSyncAlt />
        </button>
      </div>

      <div className="ee-note">
        <FaExclamationTriangle />
        <div>
          <p><strong>Note:</strong> This checklist is a personal guide to help you track what needs to be completed over your 3-year course. It is <strong>not</strong> a formal exam eligibility gate — you will not be barred from appearing in an exam solely based on this checklist. Final eligibility is always determined by the institution.</p>
          <p>Other criteria such as attendance ≥80%, mandatory training period, and proper record maintenance are verified separately by the institution during final eligibility processing.</p>
        </div>
      </div>

      {/* Overall status banner */}
      <div className={`ee-banner ${overallEligible ? 'eligible' : 'not-eligible'}`}>
        <div className="ee-banner-icon">
          {overallEligible ? <FaCheckCircle /> : <FaTimesCircle />}
        </div>
        <div className="ee-banner-text">
          <h3>{overallEligible ? 'Eligible for Exam' : 'Not Yet Eligible'}</h3>
          {overallEligible
            ? <p>All eligibility criteria are satisfied.</p>
            : <p>{missingItems.length} requirement{missingItems.length > 1 ? 's' : ''} pending</p>
          }
        </div>
      </div>

      {!overallEligible && missingItems.length > 0 && (
        <div className="ee-missing-list">
          <h4><FaExclamationTriangle /> Pending Requirements</h4>
          <ul>
            {missingItems.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      )}

      <div className="ee-checks-section">
        <h3 className="ee-section-title">Eligibility Checklist</h3>

        {/* 1. Weekly Logbook */}
        {checks.weeklyLogbook.applicable !== false && (
          <CheckRow
            icon={<FaBookMedical />}
            title="Weekly Logbook Submissions"
            description="Regular weekly e-logbook entries required"
            passed={checks.weeklyLogbook.passed}
            detail={`${checks.weeklyLogbook.submittedWeeks} of last 12 weeks submitted (${checks.weeklyLogbook.rate}%) — minimum 75% required`}
          />
        )}

        {/* 2. DRP */}
        <CheckRow
          icon={<FaHospital />}
          title="District Residency Programme (DRP)"
          description="3-month compulsory district residency with satisfactory completion certificate"
          passed={checks.drpCompletion.passed}
          detail={checks.drpCompletion.passed
            ? (checks.drpCompletion.mode === 'classic'
                ? (checks.drpCompletion.details?.issued
                    ? 'Classic DRP certificate issued by system'
                    : 'Classic DRP certificate uploaded')
                : `${checks.drpCompletion.details?.completedPostings} completed posting(s)`)
            : 'DRP completion certificate not found'
          }
        />

        {/* 3. Research Methodology Certificate */}
        <CheckRow
          icon={<FaAward />}
          title="Research Methodology Certificate"
          description="Must complete in 1st year — certificate upload required"
          passed={checks.certificates.researchMethodology.passed}
          detail={checks.certificates.researchMethodology.passed ? 'Certificate uploaded' : 'Certificate not uploaded'}
        />

        {/* 4. Ethics Course Certificate */}
        <CheckRow
          icon={<FaAward />}
          title="Ethics Course Certificate"
          description="Mandatory before exam — certificate upload required"
          passed={checks.certificates.ethicsCourse.passed}
          detail={checks.certificates.ethicsCourse.passed ? 'Certificate uploaded' : 'Certificate not uploaded'}
        />

        {/* 5. BCLS & ACLS Certificate */}
        <CheckRow
          icon={<FaAward />}
          title="BCLS & ACLS Certificate"
          description="Cardiac Life Support certification required"
          passed={checks.certificates.bclsAcls.passed}
          detail={checks.certificates.bclsAcls.passed ? 'Certificate uploaded' : 'Certificate not uploaded'}
        />

        {/* 6. Dissertation */}
        <CheckRow
          icon={<FaGraduationCap />}
          title="Thesis / Dissertation"
          description="All submitted progress reports must be graded by supervisor"
          passed={checks.dissertation.passed}
          detail={checks.dissertation.totalPeriods === 0
            ? (checks.dissertation.topicName ? 'No progress reports submitted yet' : 'No dissertation submitted')
            : `${checks.dissertation.topicName ? `"${checks.dissertation.topicName}" — ` : ''}${checks.dissertation.gradedPeriods}/${checks.dissertation.totalPeriods} progress reports graded`
          }
        />

        {/* 7. Publications */}
        <CheckRow
          icon={<FaNewspaper />}
          title="Research Publication / Presentation"
          description="At least one: poster, conference paper, or research publication as first author"
          passed={checks.publications.passed}
          detail={checks.publications.passed
            ? `${checks.publications.count} publication(s) / presentation(s) recorded`
            : 'No publication or presentation recorded'
          }
        />

        {/* 8. Seminars & Activities */}
        <CheckRow
          icon={<FaClipboardList />}
          title="Seminars, Journal Clubs & Clinical Meetings"
          description="Active participation in seminars, journal clubs, case discussions, clinical meetings"
          passed={checks.seminarsActivities.passed}
          notApplicable={!checks.seminarsActivities.applicable}
          detail={!checks.seminarsActivities.applicable
            ? 'No matching modules in your course'
            : checks.seminarsActivities.passed
              ? `Entries found in: ${checks.seminarsActivities.matchedModules.join(', ')}`
              : `No entries in: ${checks.seminarsActivities.matchedModules.join(', ')}`
          }
        />

        {/* 9. UG Teaching */}
        <CheckRow
          icon={<FaUsers />}
          title="UG Teaching Participation"
          description="PG students must participate in teaching undergraduate students & interns"
          passed={checks.ugTeaching.passed}
          notApplicable={!checks.ugTeaching.applicable}
          detail={!checks.ugTeaching.applicable
            ? 'No UG teaching module in your course'
            : checks.ugTeaching.passed
              ? `Entries found in: ${checks.ugTeaching.matchedModules.join(', ')}`
              : `No entries in: ${checks.ugTeaching.matchedModules.join(', ')}`
          }
        />
      </div>

    </div>
  );
};

export default ExamEligibility;
